import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnnualTimetableCell, Grade } from '../types/domain';
import { useCalendarLinkedContext } from './useCalendarLinkedContext';
import {
  isClassLessonAlarmTimePassed,
  type ClassLessonAlarmPopupInfo,
} from '../components/classAlarm/ClassLessonAlarmPopup';
import {
  buildTodayClassLessonAlarmTargets,
  ensureClassLessonAlarmData,
} from '../utils/classLessonAlarmSchedule';
import {
  CLASS_LESSON_ALARM_ATTENTION_EVENT,
  CLASS_LESSON_ALARM_REOPEN_EVENT,
  CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT,
  CLASS_LESSON_ALARM_TEST_FIRE_EVENT,
  loadClassLessonAlarmSettings,
  type ClassLessonAlarmAttentionDetail,
  type ClassLessonAlarmOffset,
  type ClassLessonAlarmReopenDetail,
  type ClassLessonAlarmSettings,
  type ClassLessonAlarmSoundKind,
  type ClassLessonAlarmTestFireDetail,
} from '../utils/classLessonAlarmSettings';
import {
  applyClassLessonAlarmCustomSchedule,
  CLASS_LESSON_ALARM_CUSTOM_SCHEDULE_CHANGED_EVENT,
  createDefaultClassLessonAlarmCustomSchedule,
  type ClassLessonAlarmCustomSchedule,
  type ClassLessonAlarmCustomScheduleChangedDetail,
} from '../utils/classLessonAlarmCustomSchedule';
import { peekClassLessonAlarmCustomSchedule } from '../utils/classLessonAlarmCustomScheduleCache';
import { getClassLessonAlarmCustomSchedule } from '../services/classLessonAlarmCustomScheduleService';
import {
  classLessonAlarmGaugeEndsAtMs,
  getMinutesBeforeClassLessonAlarm,
  isClassLessonAlarmTriggerLate,
  playClassLessonAlarmSound,
  preloadClassLessonAlarmSounds,
  stopAllClassLessonAlarmSounds,
} from '../utils/classLessonAlarmSound';
import {
  openClassLessonAlarmPopupRelay,
  postClassLessonAlarmRelay,
  type ClassLessonAlarmRelayMessage,
} from '../utils/classLessonAlarmPopupRelay';
import {
  addClassLessonAlarmDismissed,
  claimClassLessonAlarmSound,
  publishClassLessonAlarmSchedule,
  readClassLessonAlarmDismissed,
  releaseClassLessonAlarmSound,
  showClassLessonAlarmDesktopNotice,
} from '../utils/classLessonAlarmCrossTab';
import { toISODate } from '../utils/date';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function offsetToMinutes(offset: ClassLessonAlarmOffset): number {
  if (offset === '1M') return 1;
  if (offset === '5M') return 5;
  return 3;
}

/** 3M → [3,2,1], 5M → [5,4,3,2,1], 1M → [1] */
function phaseMinutesList(offset: ClassLessonAlarmOffset): number[] {
  if (offset === '1M') return [1];
  if (offset === '3M') return [3, 2, 1];
  return [5, 4, 3, 2, 1];
}

function formatHHMM(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function dispatchAttention(workspaceId: string, active: boolean): void {
  window.dispatchEvent(
    new CustomEvent(CLASS_LESSON_ALARM_ATTENTION_EVENT, {
      detail: { workspaceId, active } satisfies ClassLessonAlarmAttentionDetail,
    })
  );
}

/**
 * 워크스페이스 전역 수업 알람 서비스.
 * 교시 시작 시각 기준 N분 전(에듀코디와 동일하게 구간별 리마인더)에 모달·사운드를 띄운다.
 */
export function useClassLessonAlarmService(params: {
  workspaceId: string;
  grades: Grade[];
  currentGradeId: string | null;
  enabled?: boolean;
}) {
  const { workspaceId, grades, currentGradeId, enabled = true } = params;
  const selectedGradeId = (currentGradeId ?? '').trim();
  /** 선택된 클래스만 알람 대상 (전체 grades가 아닌 현재 TopNav 선택 반) */
  const selectedGrades = useMemo(() => {
    if (!selectedGradeId) return [] as Grade[];
    return grades.filter((g) => g.id === selectedGradeId);
  }, [grades, selectedGradeId]);
  const calendarCtx = useCalendarLinkedContext(workspaceId, selectedGradeId);
  const [settings, setSettings] = useState<ClassLessonAlarmSettings>(() =>
    loadClassLessonAlarmSettings(workspaceId)
  );
  const [customSchedule, setCustomSchedule] = useState<ClassLessonAlarmCustomSchedule>(() =>
    peekClassLessonAlarmCustomSchedule(workspaceId)
  );
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupInfo, setPopupInfo] = useState<ClassLessonAlarmPopupInfo | null>(null);
  const [dismissedAlarms, setDismissedAlarms] = useState<Set<string>>(() => new Set());
  const triggeredRef = useRef<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const customScheduleRef = useRef(customSchedule);
  customScheduleRef.current = customSchedule;
  const ensuredRef = useRef<{
    academicYear: number;
    termNumber: 1 | 2;
    cellsByKey: Record<string, AnnualTimetableCell>;
  } | null>(null);
  /** 팝업닫기로 최소화 — 이후 분 단위 소리는 유지, 팝업은 자동으로 다시 열지 않음 */
  const minimizedRef = useRef(false);
  const phaseTimerIdsRef = useRef<number[]>([]);
  const popupInfoRef = useRef(popupInfo);
  popupInfoRef.current = popupInfo;
  const popupOpenRef = useRef(popupOpen);
  popupOpenRef.current = popupOpen;
  /** 다른 탭(프레젠테이션 등)으로 팝업 상태를 중계하는 채널 */
  const relayChannelRef = useRef<BroadcastChannel | null>(null);
  /** 프레젠테이션 탭이 살아있는지(heartbeat) — '프레젠테이션 전용' 표시 판단용 */
  const [presentationActive, setPresentationActive] = useState(false);
  const lastPresenceAtRef = useRef(0);

  const clearPhaseTimers = useCallback(() => {
    phaseTimerIdsRef.current.forEach((id) => window.clearTimeout(id));
    phaseTimerIdsRef.current = [];
  }, []);

  const playPhaseSound = useCallback(
    (offset: ClassLessonAlarmOffset, soundKind: ClassLessonAlarmSoundKind, minutesBefore: number) => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch {
          /* ignore */
        }
        audioRef.current = null;
      }
      stopAllClassLessonAlarmSounds();
      const audio = playClassLessonAlarmSound(offset, soundKind, minutesBefore);
      audioRef.current = audio;
      audio.loop = false;
      /** false = autoplay 제한 등으로 재생 실패 */
      return audio.play().then(
        () => true,
        () => false
      );
    },
    []
  );

  /** 첫 구간 소리는 이미 재생했다고 가정하고, 이후 분(startPhase-1 … 1)을 60초 간격으로 예약 */
  const scheduleRemainingPhaseSounds = useCallback(
    (
      offset: ClassLessonAlarmOffset,
      soundKind: ClassLessonAlarmSoundKind,
      startPhaseMinutes: number
    ) => {
      clearPhaseTimers();
      const phases = phaseMinutesList(offset).filter((m) => m < startPhaseMinutes && m >= 1);
      for (const m of phases) {
        const delayMs = (startPhaseMinutes - m) * 60 * 1000;
        const id = window.setTimeout(() => {
          playPhaseSound(offset, soundKind, m);
        }, delayMs);
        phaseTimerIdsRef.current.push(id);
      }
    },
    [clearPhaseTimers, playPhaseSound]
  );

  const stopSound = useCallback(() => {
    clearPhaseTimers();
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      audioRef.current = null;
    }
    stopAllClassLessonAlarmSounds();
  }, [clearPhaseTimers]);

  const setAttention = useCallback(
    (active: boolean) => {
      const sid = workspaceId.trim();
      if (!sid) return;
      dispatchAttention(sid, active);
    },
    [workspaceId]
  );

  useEffect(() => {
    setSettings(loadClassLessonAlarmSettings(workspaceId));
    setCustomSchedule(peekClassLessonAlarmCustomSchedule(workspaceId));
    triggeredRef.current = new Set();
    setDismissedAlarms(new Set());
    ensuredRef.current = null;
    minimizedRef.current = false;
    clearPhaseTimers();
    setAttention(false);
    let cancelled = false;
    void getClassLessonAlarmCustomSchedule(workspaceId, { forceRefresh: true })
      .then((schedule) => {
        if (!cancelled) setCustomSchedule(schedule);
      })
      .catch(() => {
        if (!cancelled) {
          setCustomSchedule(peekClassLessonAlarmCustomSchedule(workspaceId));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clearPhaseTimers, setAttention, workspaceId]);

  useEffect(() => {
    if (!enabled || !workspaceId.trim()) return undefined;
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string }>).detail;
      if ((d?.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      const next = loadClassLessonAlarmSettings(workspaceId);
      setSettings(next);
      if (!next.masterEnabled) {
        stopSound();
        minimizedRef.current = false;
        setPopupOpen(false);
        setPopupInfo(null);
        setAttention(false);
      }
    };
    const onCustomChanged = (e: Event) => {
      const d = (e as CustomEvent<ClassLessonAlarmCustomScheduleChangedDetail>).detail;
      if ((d?.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      setCustomSchedule(peekClassLessonAlarmCustomSchedule(workspaceId));
    };
    window.addEventListener(CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT, onChanged as EventListener);
    window.addEventListener(
      CLASS_LESSON_ALARM_CUSTOM_SCHEDULE_CHANGED_EVENT,
      onCustomChanged as EventListener
    );
    return () => {
      window.removeEventListener(
        CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT,
        onChanged as EventListener
      );
      window.removeEventListener(
        CLASS_LESSON_ALARM_CUSTOM_SCHEDULE_CHANGED_EVENT,
        onCustomChanged as EventListener
      );
    };
  }, [enabled, setAttention, stopSound, workspaceId]);

  useEffect(() => {
    if (!enabled) return undefined;
    void preloadClassLessonAlarmSounds();
    return () => {
      stopAllClassLessonAlarmSounds();
      clearPhaseTimers();
    };
  }, [clearPhaseTimers, enabled]);

  const academicYear = calendarCtx.academicYear;
  const termNumber = useMemo((): 1 | 2 => {
    const todayIso = toISODate(new Date());
    const hit = (calendarCtx.terms ?? []).find((t) => {
      const from = (t.starts_on ?? '').trim();
      const to = (t.ends_on ?? '').trim();
      return Boolean(from && to && todayIso >= from && todayIso <= to);
    });
    return hit?.term_number === 2 ? 2 : 1;
  }, [calendarCtx.terms]);

  useEffect(() => {
    if (!enabled || !workspaceId.trim()) return undefined;
    const onTestFire = (e: Event) => {
      const d = (e as CustomEvent<ClassLessonAlarmTestFireDetail>).detail;
      if (!d || (d.workspaceId ?? '').trim() !== workspaceId.trim()) return;

      const offset = d.offset;
      const soundKind: ClassLessonAlarmSoundKind = d.soundKind === 'activity' ? 'activity' : 'basic';
      const phaseMinutes = offsetToMinutes(offset);
      const now = new Date();
      const startAt = new Date(now.getTime() + phaseMinutes * 60 * 1000);
      const time = formatHHMM(startAt);

      stopSound();
      minimizedRef.current = false;
      setPopupInfo({
        time,
        offset,
        phaseMinutes,
        channelLabel: '테스트',
        name: `${phaseMinutes}분 전 알람 실행`,
        startsAtMs: startAt.getTime(),
      });
      setPopupOpen(true);
      setAttention(false);

      // START 즉시 + 이후 분마다 소리 (게이지는 phaseMinutes 동안 연속)
      playPhaseSound(offset, soundKind, phaseMinutes);
      scheduleRemainingPhaseSounds(offset, soundKind, phaseMinutes);
    };
    window.addEventListener(CLASS_LESSON_ALARM_TEST_FIRE_EVENT, onTestFire as EventListener);
    return () => {
      window.removeEventListener(CLASS_LESSON_ALARM_TEST_FIRE_EVENT, onTestFire as EventListener);
    };
  }, [
    enabled,
    playPhaseSound,
    scheduleRemainingPhaseSounds,
    setAttention,
    stopSound,
    workspaceId,
  ]);

  useEffect(() => {
    if (!enabled || !workspaceId.trim()) return undefined;
    const onReopen = (e: Event) => {
      const d = (e as CustomEvent<ClassLessonAlarmReopenDetail>).detail;
      if (!d || (d.workspaceId ?? '').trim() !== workspaceId.trim()) return;
      const info = popupInfoRef.current;
      if (!info) return;
      if (isClassLessonAlarmTimePassed(info.time)) {
        setPopupInfo(null);
        minimizedRef.current = false;
        setAttention(false);
        return;
      }
      minimizedRef.current = false;
      setPopupOpen(true);
      setAttention(false);
    };
    window.addEventListener(CLASS_LESSON_ALARM_REOPEN_EVENT, onReopen as EventListener);
    return () => {
      window.removeEventListener(CLASS_LESSON_ALARM_REOPEN_EVENT, onReopen as EventListener);
    };
  }, [enabled, setAttention, workspaceId]);

  const handleStopAlarm = useCallback(() => {
    stopSound();
    if (popupInfo) {
      const alarmKey = `${popupInfo.time}-${popupInfo.offset}`;
      setDismissedAlarms((prev) => new Set(prev).add(alarmKey));
      addClassLessonAlarmDismissed(workspaceId, toISODate(new Date()), alarmKey);
    }
    minimizedRef.current = false;
    setPopupOpen(false);
    setPopupInfo(null);
    setAttention(false);
  }, [popupInfo, setAttention, stopSound, workspaceId]);

  const handleClosePopup = useCallback(() => {
    // 팝업만 닫음 — 분 단위 소리는 계속. 네비바 종이 두근두근.
    minimizedRef.current = true;
    setPopupOpen(false);
    setAttention(true);
  }, [setAttention]);

  const handleReopenPopup = useCallback(() => {
    if (!popupInfo) return;
    if (isClassLessonAlarmTimePassed(popupInfo.time)) {
      setPopupInfo(null);
      minimizedRef.current = false;
      setAttention(false);
      return;
    }
    minimizedRef.current = false;
    setPopupOpen(true);
    setAttention(false);
  }, [popupInfo, setAttention]);

  /** 크로스탭 중계: 채널 열기 + 프레젠테이션 탭의 동기화 요청·액션 수신 */
  useEffect(() => {
    if (!enabled || !workspaceId.trim()) return undefined;
    const channel = openClassLessonAlarmPopupRelay(workspaceId);
    relayChannelRef.current = channel;
    if (!channel) return undefined;
    const onMessage = (e: MessageEvent<ClassLessonAlarmRelayMessage>) => {
      const msg = e.data;
      if (!msg) return;
      if (msg.kind === 'request-sync') {
        postClassLessonAlarmRelay(channel, {
          kind: 'state',
          open: popupOpenRef.current,
          info: popupInfoRef.current,
        });
      } else if (msg.kind === 'action') {
        if (msg.action === 'stop') handleStopAlarm();
        else if (msg.action === 'close') handleClosePopup();
      } else if (msg.kind === 'presence') {
        if (msg.alive) {
          lastPresenceAtRef.current = Date.now();
          setPresentationActive(true);
        } else {
          lastPresenceAtRef.current = 0;
          setPresentationActive(false);
        }
      }
    };
    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      relayChannelRef.current = null;
    };
  }, [enabled, workspaceId, handleStopAlarm, handleClosePopup]);

  /** heartbeat 만료 감시: 5초 이상 신호 없으면 프레젠테이션 닫힘으로 간주 */
  useEffect(() => {
    if (!enabled || !workspaceId.trim()) return undefined;
    const id = window.setInterval(() => {
      if (lastPresenceAtRef.current && Date.now() - lastPresenceAtRef.current > 5000) {
        lastPresenceAtRef.current = 0;
        setPresentationActive(false);
      }
    }, 2000);
    return () => window.clearInterval(id);
  }, [enabled, workspaceId]);

  /** 팝업 상태 변경 시 다른 탭으로 브로드캐스트 */
  useEffect(() => {
    postClassLessonAlarmRelay(relayChannelRef.current, {
      kind: 'state',
      open: popupOpen,
      info: popupInfo,
    });
  }, [popupOpen, popupInfo]);

  const checkAlarms = useCallback(() => {
    if (!enabled) return;
    if (!settingsRef.current.masterEnabled) return;
    const sid = workspaceId.trim();
    if (!sid || !selectedGrades.length) return;

    const now = new Date();
    const todayIso = toISODate(now);
    const ensured = ensuredRef.current;
    const ay = ensured?.academicYear ?? academicYear;
    const tn = ensured?.termNumber ?? termNumber;
    const targets = applyClassLessonAlarmCustomSchedule(
      buildTodayClassLessonAlarmTargets({
        workspaceId: sid,
        grades: selectedGrades,
        academicYear: ay,
        termNumber: tn,
        settings: settingsRef.current,
        now,
        ringOnly: true,
        cellsByKey: ensured?.cellsByKey,
      }),
      customScheduleRef.current ?? createDefaultClassLessonAlarmCustomSchedule()
    );
    publishClassLessonAlarmSchedule(
      sid,
      todayIso,
      targets.map((t) => ({
        time: t.time,
        offset: t.offset,
        soundKind: t.soundKind,
        channel: t.channel,
        gradeLabel: t.gradeLabel,
        periodLabel: t.periodLabel,
      }))
    );
    const sharedDismissed = readClassLessonAlarmDismissed(sid, todayIso);

    const dayKey = `${todayIso}`;
    Array.from(triggeredRef.current).forEach((key) => {
      if (!key.startsWith(dayKey)) triggeredRef.current.delete(key);
    });

    // 최소화 세션이 교시 시작을 지났으면 정리 (팝업이 열려 있으면 자동 종료하지 않음)
    const active = popupInfoRef.current;
    if (active && minimizedRef.current && isClassLessonAlarmTimePassed(active.time, now)) {
      minimizedRef.current = false;
      setPopupOpen(false);
      setPopupInfo(null);
      setAttention(false);
      clearPhaseTimers();
    }

    for (const target of targets) {
      const minutesBefore = getMinutesBeforeClassLessonAlarm(target.time, target.offset, now);
      if (minutesBefore == null) continue;

      const dismissKey = `${target.time}-${target.offset}`;
      if (dismissedAlarms.has(dismissKey) || sharedDismissed.has(dismissKey)) continue;

      const triggerKey = `${dayKey}-${target.time}-${target.offset}-${minutesBefore}-${target.channel}`;
      if (triggeredRef.current.has(triggerKey)) continue;
      triggeredRef.current.add(triggerKey);

      const channelLabel = target.channel === 'dedicated' ? '전담수업' : '일반수업';
      const isSameSession =
        popupInfoRef.current &&
        popupInfoRef.current.time === target.time &&
        popupInfoRef.current.offset === target.offset;

      const lateLastPhase = minutesBefore === 1 && isClassLessonAlarmTriggerLate(now);
      const gaugeEndsAtMs = classLessonAlarmGaugeEndsAtMs(target.time, minutesBefore, now);
      setPopupInfo((prev) => {
        if (prev && prev.time === target.time && prev.offset === target.offset) {
          return {
            ...prev,
            channelLabel,
            name: `${target.gradeLabel} ${target.periodLabel}`,
            ...(lateLastPhase ? { startsAtMs: gaugeEndsAtMs } : {}),
          };
        }
        return {
          time: target.time,
          offset: target.offset,
          phaseMinutes: minutesBefore,
          channelLabel,
          name: `${target.gradeLabel} ${target.periodLabel}`,
          startsAtMs: gaugeEndsAtMs,
        };
      });

      // 새 세션이면 팝업 오픈. 최소화 중이면 소리만(게이지는 백그라운드 유지).
      if (!isSameSession) {
        minimizedRef.current = false;
        setPopupOpen(true);
        setAttention(false);
      } else if (!minimizedRef.current) {
        setPopupOpen(true);
        setAttention(false);
      }

      // 구간 시작을 놓쳐 늦게 열린 경우 이 구간 소리는 생략 — 다음 분 구간에서 울림(마지막 1분 구간은 늦어도 울림)
      if (isClassLessonAlarmTriggerLate(now) && minutesBefore > 1) break;

      const alarmName = `${target.gradeLabel} ${target.periodLabel}`;
      void claimClassLessonAlarmSound(sid, triggerKey).then(async (won) => {
        if (!won || !settingsRef.current.masterEnabled) return;
        if (readClassLessonAlarmDismissed(sid, todayIso).has(dismissKey)) return;
        const played = await playPhaseSound(target.offset, target.soundKind, minutesBefore);
        if (!played) releaseClassLessonAlarmSound(sid, triggerKey);
        if (document.visibilityState !== 'visible') {
          showClassLessonAlarmDesktopNotice({
            title: `수업 알람 · ${minutesBefore}분 전`,
            body: `${alarmName} (${target.time} 시작)`,
            tag: `cp-class-lesson-alarm:${todayIso}-${target.time}-${target.offset}`,
            onClick: () => {
              minimizedRef.current = false;
              setPopupOpen(true);
              setAttention(false);
            },
          });
        }
      });
      break;
    }
  }, [
    academicYear,
    clearPhaseTimers,
    dismissedAlarms,
    enabled,
    playPhaseSound,
    selectedGrades,
    setAttention,
    termNumber,
    workspaceId,
  ]);

  /** 클래스 전환 시 다른 반 알람 잔존 방지 */
  useEffect(() => {
    triggeredRef.current = new Set();
    setDismissedAlarms(new Set());
    ensuredRef.current = null;
    minimizedRef.current = false;
    setPopupOpen(false);
    setPopupInfo(null);
    setAttention(false);
    clearPhaseTimers();
    stopAllClassLessonAlarmSounds();
  }, [selectedGradeId, clearPhaseTimers, setAttention]);

  useEffect(() => {
    if (!enabled || !workspaceId.trim() || !settings.masterEnabled) return undefined;
    if (!selectedGrades.length) return undefined;
    let cancelled = false;
    void ensureClassLessonAlarmData({
      workspaceId,
      grades: selectedGrades,
      programSidebarAcademicYear: academicYear,
    }).then((ensured) => {
      if (cancelled) return;
      ensuredRef.current = {
        academicYear: ensured.academicYear,
        termNumber: ensured.termNumber,
        cellsByKey: ensured.cellsByKey,
      };
      checkAlarms();
    });
    return () => {
      cancelled = true;
    };
  }, [
    academicYear,
    checkAlarms,
    enabled,
    selectedGrades,
    settings.masterEnabled,
    workspaceId,
  ]);

  useEffect(() => {
    if (!enabled || !workspaceId.trim() || !settings.masterEnabled) return undefined;
    const interval = window.setInterval(checkAlarms, 1000);
    return () => window.clearInterval(interval);
  }, [checkAlarms, enabled, settings.masterEnabled, workspaceId]);

  useEffect(() => {
    if (!enabled || !settings.masterEnabled) return undefined;
    const tick = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0 && now.getSeconds() < 2) {
        setDismissedAlarms(new Set());
        triggeredRef.current = new Set();
      }
    };
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [enabled, settings.masterEnabled]);

  return {
    popupOpen,
    popupInfo,
    handleStopAlarm,
    handleClosePopup,
    handleReopenPopup,
    settings,
    masterEnabled: settings.masterEnabled,
    presentationActive,
    academicYear,
    termNumber,
  };
}
